/* eslint semi: 1 */
import { defineStore } from 'pinia';
import { markRaw } from 'vue';
import { DateTime } from 'luxon';
import { proxyException } from '@/utils/communication';
import { useMediaApi } from '@base-shared/composables/use-api';
import CONST from '@/globals/constants';
import type { components, operations } from '@base-shared/types/media.openapi';
import type { AxiosError } from 'axios';
import type { MediaPageContextValue } from '@/pinia/stores/media/mediaQueryStore';

type ListMediaResponse = components['schemas']['ListMediaResponse'];
type ListMediaQuery = operations['listMedia']['parameters']['query'];
type MediaListItem = components['schemas']['MediaItem'];

/** Returns true if an axios error was caused by an AbortController abort */
function isCancelledError (error: unknown): boolean {
  return (error as any)?.code === 'ERR_CANCELED' || (error as any)?.name === 'CanceledError';
}

export type LEDMediaCanvas = {
  media: MediaListItem[];
  previewWidth?: number;
  lastFetchedAt: string | null;
};
export type LEDMediaCanvasMap = Map<string, LEDMediaCanvas>;
interface MediaListState {
  pageIndex: number;

  isFetchingFilesPage: boolean;
  filesPageMedia: MediaListItem[];
  filesPageError: AxiosError | null;
  filesPageLastFetchedAt: string | null;

  isFetchingAlertsPage: boolean;
  alertsPageMedia: MediaListItem[];
  alertsPageError: AxiosError | null;
  alertsPageLastFetchedAt: string | null;

  isFetchingLeds: boolean;
  ledMediaCanvasMap: LEDMediaCanvasMap;
  ledPageError: AxiosError | null;

  isFetchingVideowall: boolean;
  videowallMediaCanvasMap: LEDMediaCanvasMap;
  videowallPageError: AxiosError | null;

  /**
   * Tracks the active AbortController used for all fetch operations.
   * Not reactive — stored via markRaw. This is used to cancel
   * in-flight fetches when navigating away from pages or initiating
   * new fetches, ensuring that we don't update state with stale
   * data after a component has unmounted or a new fetch has started.
   */
  abortController: AbortController | null;

  /**
   * State for the "scheduled" filter, which is a special case that can apply
   * on top of any media type and has its own API endpoint. This state is managed
   * separately from the main media lists to allow it to be applied and removed
   * without losing the underlying media list data, and to handle its own
   * fetching and error states.
   */
  scheduledFilterMedia: {
    value: boolean | null; // true | false | null (only scheduled, only non-scheduled, or no filter)
    mediaItems: MediaListItem[];
    isFetching: boolean;
    pageContext: null | MediaPageContextValue; // the pageContext where the loaded filter was applied to.
    ledDimension?: string; // the LED dimension where the loaded filter was applied to.
    abortController: AbortController | null;
    error: AxiosError | null;
  },

  /**
   * Holds confirmed media item updates keyed by resourceId, kept separate
   * from the canonical media lists so the master list does not immediately
   * re-render on every update. Updates are staged here after they are
   * successfully applied on the server; the component overlays them on top
   * of the displayed items for the current cycle.
   * Call mergeStagedMediaUpdates() to write the staged updates back into
   * the canonical lists on the next cycle (e.g. filter change, sort change,
   * or manual refresh).
   */
  stagedMediaUpdates: Map<string, MediaListItem>;
}

export const DEFAULT_MAX_ITEMS_COUNT = 24;
export const DEFAULT_MAX_ITEMS_COUNT_FILES_PAGE = 1000; // Higher count for files page
export const DEFAULT_REQUEST_PARAMS = {
  max: DEFAULT_MAX_ITEMS_COUNT,
  validity: '1' as components['parameters']['validity'],
  width: 424,
  use_qsa: '1' as components['parameters']['use_qsa']
};

export const useMediaListStore = defineStore('media-list-store', {
  state: (): MediaListState => ({
    pageIndex: 0,

    isFetchingFilesPage: false,
    filesPageMedia: [] as MediaListItem[],
    filesPageError: null,
    filesPageLastFetchedAt: null,

    isFetchingAlertsPage: false,
    alertsPageMedia: [] as MediaListItem[],
    alertsPageError: null,
    alertsPageLastFetchedAt: null,

    isFetchingLeds: false,
    ledMediaCanvasMap: new Map(),
    ledPageError: null,

    isFetchingVideowall: false,
    videowallMediaCanvasMap: new Map(),
    videowallPageError: null,

    abortController: null,

    scheduledFilterMedia: {
      value: null,
      mediaItems: [],
      isFetching: false,
      pageContext: null,
      ledDimension: undefined,
      abortController: null,
      error: null
    },

    stagedMediaUpdates: new Map()
  }),
  actions: {
    /**
     * Mutate the current page index (used for pagination controls on the files page).
     * @param index - The new page index to set (starting from 0)
     */
    setPageIndex (index: number) {
      this.pageIndex = index;
    },
    /**
     * Initializes a new AbortController for all the fetch,
     * aborting any in-flight fetch if it exists.
     * @returns {AbortSignal} The signal of the new AbortController.
     */
    initNewAbortController () {
      this.abortController?.abort();
      this.abortController = markRaw(new AbortController());
      return this.abortController.signal;
    },
    /**
     * Cancels any in-flight fetchAccumulatedPages loop by aborting the
     * current AbortController. Safe to call even if no fetch is running.
     */
    cancelFetch () {
      this.abortController?.abort();
      this.abortController = null;
    },
    /**
     * Pure fetch helper — calls the API and returns the raw result.
     * Does not mutate state. Use loadPage() for stateful page loading.
     * @param {Object} params - API request params
     * @param {AbortSignal} signal - Optional AbortSignal to support cancellation of the entire fetch loop.
     * @returns {Object} An object containing the media items,
     * a success flag, a cancellation flag, and any error encountered.
     */
    async fetchPage (params: ListMediaQuery = {}, signal?: AbortSignal): Promise<{
      success: boolean;
      cancelled: boolean;
      error: AxiosError | null;
      response: ListMediaResponse | null;
    }> {
      const api = useMediaApi();
      const requestParams = { ...DEFAULT_REQUEST_PARAMS, ...params };
      const { ok: response, error } = await proxyException(
        () => api.getMediaList(requestParams, signal)
      );

      return {
        success: !error,
        cancelled: isCancelledError(error),
        error,
        response: response?.data ?? null
      };
    },

    /**
     * Fetches all pages of media items based on the provided
     * query parameters, accumulating results into a single list.
     * @param {ListMediaQuery} baseParams - API request params to apply to all page fetches (filters, etc.)
     * @param {AbortSignal} signal - Optional AbortSignal to support cancellation of the entire fetch loop.
     * @returns {Object} An object containing the accumulated media items,
     * a success flag, a cancellation flag, and any error encountered.
     */
    async fetchAccumulatedPages (baseParams: ListMediaQuery = {}, signal?: AbortSignal): Promise<{
      success: boolean;
      cancelled: boolean;
      error: AxiosError | null;
      media: MediaListItem[];
    }> {
      let accumulatedMedia: MediaListItem[] = [];
      let nextToken: string | undefined;

      do {
        const params = { ...baseParams, nextToken };
        const { response, error } = await this.fetchPage(params, signal);
        if (error) {
          // Stop fetching further pages on error and return
          // what we have so far along with the error
          return { success: false, cancelled: isCancelledError(error), error, media: accumulatedMedia };
        }

        accumulatedMedia = accumulatedMedia.concat(response?.media ?? []);
        nextToken = response?.nextToken;
      } while (nextToken);

      return { success: true, cancelled: false, error: null, media: accumulatedMedia, };
    },

    /**
     * Prepends a new item across all cached media items, matched by `resourceId`.
     * Depending on the presence of the `led` property, the new item is prepended
     * to either the LED/Videowall or the files page media list.
     * Used after a successful duplicate to put the copy at the top of the list
     * without a full re-fetch.
     * @param {Object} newItem - The newly created media item (must have resourceId)
     */
    prependItem (newItem: MediaListItem) {
      if (
        this.filesPageMedia.some(item => item.resourceId === newItem.resourceId) ||
        this.alertsPageMedia.some(item => item.resourceId === newItem.resourceId) ||
        (newItem.led && this.ledMediaCanvasMap.get(newItem.led)?.media.some(item => item.resourceId === newItem.resourceId)) ||
        (newItem.led && this.videowallMediaCanvasMap.get(newItem.led)?.media.some(item => item.resourceId === newItem.resourceId))
      ) {
        // Avoid prepending if the item already exists in the cached media items
        this.updateItem(newItem);
        return;
      }

      if (newItem.led) {
        // For media item with `led` property, we only prepend
        // if the dimension exists and the new item's LED tag
        // matches the currently viewed dimension.
        if (this.ledMediaCanvasMap?.size && this.ledMediaCanvasMap.has(newItem.led)) {
          const canvas = this.ledMediaCanvasMap.get(newItem.led);
          canvas?.media.unshift(newItem);
        }

        if (this.videowallMediaCanvasMap?.size && this.videowallMediaCanvasMap.has(newItem.led)) {
          const canvas = this.videowallMediaCanvasMap.get(newItem.led);
          canvas?.media.unshift(newItem);
        }

        return;
      }

      // For media items without `led` property, we prepend to
      // the files and alerts page list
      if (this.filesPageMedia) this.filesPageMedia.unshift(newItem);
      if (this.alertsPageMedia && newItem.alert) this.alertsPageMedia.unshift(newItem);
    },

    /**
     * Replaces a single item across all cached media items, matched by `resourceId`.
     * @param {Object} updatedItem - The updated media item (must have resourceId)
     */
    updateItem (updatedItem: MediaListItem) {
      const updatedResourceId = updatedItem.resourceId;

      if (this.filesPageMedia?.length) {
        const index = this.filesPageMedia.findIndex((item) => item.resourceId === updatedResourceId);
        if (index !== -1) this.filesPageMedia.splice(index, 1, updatedItem);
      }

      if (this.alertsPageMedia?.length) {
        const index = this.alertsPageMedia.findIndex((item) => item.resourceId === updatedResourceId);
        if (index !== -1) this.alertsPageMedia.splice(index, 1, updatedItem);
      }

      if (this.ledMediaCanvasMap?.size) {
        this.ledMediaCanvasMap.forEach((canvas) => {
          const index = canvas.media.findIndex((item) => item.resourceId === updatedResourceId);
          if (index !== -1) canvas.media.splice(index, 1, updatedItem);
        });
      }

      if (this.videowallMediaCanvasMap?.size) {
        this.videowallMediaCanvasMap.forEach((canvas) => {
          const index = canvas.media.findIndex((item) => item.resourceId === updatedResourceId);
          if (index !== -1) canvas.media.splice(index, 1, updatedItem);
        });
      }
    },

    /**
     * Removes a single item from all cached media items, matched by `resourceId`.
     * @param {string} resourceId
     */
    removeItem (resourceId: string) {
      if (this.filesPageMedia?.length) {
        const index = this.filesPageMedia.findIndex((item) => item.resourceId === resourceId);
        if (index !== -1) this.filesPageMedia.splice(index, 1);
      }

      if (this.alertsPageMedia?.length) {
        const index = this.alertsPageMedia.findIndex((item) => item.resourceId === resourceId);
        if (index !== -1) this.alertsPageMedia.splice(index, 1);
      }

      if (this.ledMediaCanvasMap?.size) {
        this.ledMediaCanvasMap.forEach((canvas) => {
          const index = canvas.media.findIndex((item) => item.resourceId === resourceId);
          if (index !== -1) canvas.media.splice(index, 1);
        });
      }

      if (this.videowallMediaCanvasMap?.size) {
        this.videowallMediaCanvasMap.forEach((canvas) => {
          const index = canvas.media.findIndex((item) => item.resourceId === resourceId);
          if (index !== -1) canvas.media.splice(index, 1);
        });
      }
    },

    /**
     * Removes an entire LED dimension from the corresponding canvas map.
     * @param dimension - The LED dimension to remove (e.g., "1920x1080")
     * @param type - The type of canvas map ("led" or "videowall")
     */
    removeLedDimension (dimension: string, type: 'led' | 'videowall') {
      if (type === 'led' && this.ledMediaCanvasMap?.size && this.ledMediaCanvasMap.has(dimension)) {
        this.ledMediaCanvasMap.delete(dimension);
      } else if (type === 'videowall' && this.videowallMediaCanvasMap?.size && this.videowallMediaCanvasMap.has(dimension)) {
        this.videowallMediaCanvasMap.delete(dimension);
      }
    },

    /**
     * Loads all media items for the "files" page, which is
     * unpaginated and shows the full list. This method handles
     * sequential fetching of all pages until the complete list is loaded,
     * accumulating results in filesPageMedia.
     */
    async loadFilesPage (): Promise<{
      success: boolean;
      cancelled: boolean;
      error: Error | AxiosError | null;
    }> {
      if (this.isFetchingFilesPage) return { success: false, cancelled: false, error: null };

      // Save the previous media list so it can be restored on cancellation.
      const previousItems = this.filesPageMedia ?? [];

      this.$patch((state) => {
        state.isFetchingFilesPage = true;
        state.filesPageError = null;
        state.filesPageMedia = [];
      });

      // Init a new AbortController for this entire fetch loop, so that
      // we can cancel the whole sequence if needed (e.g. on component unmount).
      const signal = this.initNewAbortController();
      const { success, cancelled, error, media: accumulatedMedia } = await this.fetchAccumulatedPages({
        max: DEFAULT_MAX_ITEMS_COUNT_FILES_PAGE
      }, signal);

      this.$patch((state) => {
        state.isFetchingFilesPage = false;
        if (cancelled) {
          // Restore the previous media list and keep the lastFetchedAt
          // timestamp unchanged, so the next initialize() call re-fetches
          // correctly.
          state.filesPageMedia = previousItems;
        } else if (error) {
          state.filesPageError = error;
        } else {
          state.filesPageMedia = accumulatedMedia;
          state.filesPageLastFetchedAt = DateTime.now().toISO();
        }
      });

      return { success, cancelled, error: cancelled ? null : error };
    },

    /**
     * Loads all media items for the "alerts" page, which is
     * unpaginated and shows the full list. This method handles
     * sequential fetching of all pages until the complete list is loaded,
     * accumulating results in alertsPageMedia.
     */
    async loadAlertsPage (): Promise<{
      success: boolean;
      cancelled: boolean;
      error: Error | AxiosError | null;
    }> {
      if (this.isFetchingAlertsPage) return { success: false, cancelled: false, error: null };

      // Save the previous media list so it can be restored on cancellation.
      const previousItems = this.alertsPageMedia ?? [];

      this.$patch((state) => {
        state.isFetchingAlertsPage = true;
        state.alertsPageError = null;
        state.alertsPageMedia = [];
      });

      // Init a new AbortController for this entire fetch loop, so that
      // we can cancel the whole sequence if needed (e.g. on component unmount).
      const signal = this.initNewAbortController();
      const { success, cancelled, error, media: accumulatedMedia } = await this.fetchAccumulatedPages({
        max: DEFAULT_MAX_ITEMS_COUNT_FILES_PAGE,
        alert: true // Important for alert specific media items
      }, signal);

      this.$patch((state) => {
        state.isFetchingAlertsPage = false;
        if (cancelled) {
          // Restore the previous media list and keep the lastFetchedAt
          // timestamp unchanged, so the next initialize() call re-fetches
          // correctly.
          state.alertsPageMedia = previousItems;
        } else if (error) {
          state.alertsPageError = error;
        } else {
          state.alertsPageMedia = accumulatedMedia;
          state.alertsPageLastFetchedAt = DateTime.now().toISO();
        }
      });

      return { success, cancelled, error: cancelled ? null : error };
    },

    /**
     * Loads media items for a specific LED dimension,
     * accumulating all pages of results.
     * @param {string} dimension - The LED dimension to load media for (e.g., "1920x1080")
     * @param {number} previewWidth - Optional preview width for the media items.
     */
    async loadLedDimension (dimension: string, previewWidth?: number): Promise<{
      success: boolean;
      cancelled: boolean;
      error: Error | AxiosError | null;
    }> {
      if (this.isFetchingLeds) return { success: false, cancelled: false, error: null };
      if (!dimension) return { success: false, cancelled: false, error: new Error('Dimension is required') };

      // Save the previous canvas state so it can be restored on cancellation,
      // ensuring the re-fetch guard (shouldPerformRefetch) behaves correctly.
      const previousCanvas = this.ledMediaCanvasMap.get(dimension) ?? null;

      // Reset media for the specific LED dimension while
      // keeping the lastFetchedAt timestamp.
      this.$patch((state) => {
        state.isFetchingLeds = true;
        state.ledPageError = null;
        state.ledMediaCanvasMap.set(dimension, {
          media: [],
          previewWidth,
          lastFetchedAt: previousCanvas?.lastFetchedAt ?? null
        });
      });

      // Init a new AbortController for this entire fetch loop, so that
      // we can cancel the whole sequence if needed (e.g. on component unmount).
      const signal = this.initNewAbortController();
      const { success, media: accumulatedMedia, cancelled, error } = await this.fetchAccumulatedPages({
        max: DEFAULT_MAX_ITEMS_COUNT_FILES_PAGE,
        led: dimension,
        ...previewWidth ? { width: previewWidth } : {}
      }, signal);

      this.$patch((state) => {
        state.isFetchingLeds = false;
        if (cancelled) {
          // Restore the previous canvas so shouldPerformRefetch works correctly
          // on the next visit.
          if (previousCanvas) {
            state.ledMediaCanvasMap.set(dimension, previousCanvas);
          } else {
            state.ledMediaCanvasMap.delete(dimension);
          }
        } else if (error) {
          state.ledPageError = error;
        } else {
          state.ledMediaCanvasMap.set(dimension, {
            media: accumulatedMedia,
            previewWidth,
            lastFetchedAt: DateTime.now().toISO()
          });
        }
      });

      return { success, cancelled, error: cancelled ? null : error };
    },

    /**
     * Reloads media items for all LED dimensions currently in
     * the ledMediaCanvasMap.
     */
    async reloadAccumulatedLeds () {
      const dimensions = Array.from(this.ledMediaCanvasMap.keys());
      // NOTE: We load sequentially to avoid overloading the
      // API with multiple requests at once, especially
      // if there are multiple pages per dimension.
      for (const dimension of dimensions) {
        const canvasMap = this.ledMediaCanvasMap.get(dimension);
        const result = await this.loadLedDimension(dimension, canvasMap?.previewWidth);
        if (result?.cancelled || result?.error) {
          return; // Early exit on cancellation or error.
        }
      }
    },

    /**
     * Loads media items for a specific LED dimension,
     * accumulating all pages of results.
     * @param {string} dimension - The LED dimension to load media for (e.g., "1920x1080")
     * @param {number} previewWidth - Optional preview width for the media items.
     */
    async loadVideowallDimension (dimension: string, previewWidth?: number): Promise<{
      success: boolean;
      cancelled: boolean;
      error: Error | AxiosError | null;
    }> {
      if (this.isFetchingVideowall) return { success: false, cancelled: false, error: null };
      if (!dimension) return { success: false, cancelled: false, error: new Error('Dimension is required') };

      // Save the previous canvas state so it can be restored on cancellation,
      // ensuring the re-fetch guard (shouldPerformRefetch) behaves correctly.
      const previousCanvas = this.videowallMediaCanvasMap.get(dimension) ?? null;

      // Reset media for the specific LED dimension while
      // keeping the lastFetchedAt timestamp.
      this.$patch((state) => {
        state.isFetchingVideowall = true;
        state.videowallPageError = null;
        state.videowallMediaCanvasMap.set(dimension, {
          media: [],
          previewWidth: undefined,
          lastFetchedAt: previousCanvas?.lastFetchedAt ?? null
        });
      });

      // Init a new AbortController for this entire fetch loop, so that
      // we can cancel the whole sequence if needed (e.g. on component unmount).
      const signal = this.initNewAbortController();
      const { success, media: accumulatedMedia, cancelled, error } = await this.fetchAccumulatedPages({
        max: DEFAULT_MAX_ITEMS_COUNT_FILES_PAGE,
        led: dimension,
        ...previewWidth ? { width: previewWidth } : {}
      }, signal);

      this.$patch((state) => {
        state.isFetchingVideowall = false;
        if (cancelled) {
          // Restore the previous canvas so shouldPerformRefetch works correctly
          // on the next visit.
          if (previousCanvas) {
            state.videowallMediaCanvasMap.set(dimension, previousCanvas);
          } else {
            state.videowallMediaCanvasMap.delete(dimension);
          }
        } else if (error) {
          state.videowallPageError = error;
        } else {
          state.videowallMediaCanvasMap.set(dimension, {
            media: accumulatedMedia,
            previewWidth,
            lastFetchedAt: DateTime.now().toISO()
          });
        }
      });

      return { success, cancelled, error: cancelled ? null : error };
    },

    /**
     * Reloads media items for all LED dimensions currently in
     * the videowallMediaCanvasMap.
     */
    async reloadAccumulatedVideowall () {
      const dimensions = Array.from(this.videowallMediaCanvasMap.keys());
      // NOTE: We load sequentially to avoid overloading the
      // API with multiple requests at once, especially
      // if there are multiple pages per dimension.
      for (const dimension of dimensions) {
        const canvasMap = this.videowallMediaCanvasMap.get(dimension);
        const result = await this.loadVideowallDimension(dimension, canvasMap?.previewWidth);
        if (result?.cancelled || result?.error) {
          return; // Early exit on cancellation or error.
        }
      }
    },

    /**
     * Fetches media items that match the "scheduled" filter,
     * which is a special case to be used when filtering media
     * by the "scheduled" filter.
     * @param isScheduled - if true/false, fetches only scheduled or only non-scheduled media items; if null, clears the filter.
     * @param pageContext - the page context where the filter is being applied (e.g., "files" or "alerts")
     * @param ledDimension - optional LED dimension to filter by, if applicable (e.g., "led" or "videowall")
     * @returns An object containing the success status, cancellation status, and any error encountered.
     */
    async loadScheduledFilter (isScheduled: boolean | null, pageContext: MediaPageContextValue, ledDimension?: string) {
      this.resetScheduledFilter();
      if (isScheduled === null) return;

      this.$patch({
        scheduledFilterMedia: {
          value: isScheduled,
          isFetching: true,
          pageContext,
          ledDimension,
          mediaItems: [],
          abortController: markRaw(new AbortController()),
          error: null
        }
      });

      const signal = this.scheduledFilterMedia.abortController?.signal ?? undefined;
      const { success, media: accumulatedMedia, cancelled, error } = await this.fetchAccumulatedPages({
        max: DEFAULT_MAX_ITEMS_COUNT_FILES_PAGE,
        led: ledDimension,
        alert: pageContext === CONST.MEDIA_COMPONENT_AS_ALERT_FILES ? true : undefined,
        scheduled: isScheduled
      }, signal);

      this.$patch((state) => {
        state.scheduledFilterMedia.isFetching = false;
        if (cancelled) {
          // Restore the previous state so shouldPerformRefetch works correctly
          // on the next visit.
          state.scheduledFilterMedia.mediaItems = [];
        } else if (error) {
          state.scheduledFilterMedia.error = error;
        } else {
          state.scheduledFilterMedia.mediaItems = accumulatedMedia;
        }
      });

      return { success, cancelled, error: cancelled ? null : error };
    },

    /**
     * Retrieves the staged update for a media item, if one exists.
     * @param resourceId - The resourceId of the media item to look up.
     * @returns The staged update object, or undefined if none exists.
     */
    getStagedMediaUpdate (resourceId: string): MediaListItem | undefined {
      return this.stagedMediaUpdates.get(resourceId);
    },

    /**
     * Stages a confirmed media item update, keeping it out of the canonical
     * list until the next reconciliation cycle.
     * @param resourceId - The resourceId of the media item.
     * @param update - The updated media item to stage.
     */
    stageMediaUpdate (update: MediaListItem) {
      if (!update.resourceId) return;
      this.stagedMediaUpdates.set(update.resourceId, update);
    },

    /**
     * Writes all staged updates into the canonical media lists and clears
     * the staging area. Call this when the component is ready to reconcile
     * the master list with the deferred updates — typically on filter change,
     * sort change, search, or manual refresh.
     */
    mergeStagedMediaUpdates () {
      this.stagedMediaUpdates.forEach((update) => this.updateItem(update));
      this.clearStagedMediaUpdates();
    },

    /** Discards all staged updates without writing them to the canonical lists. */
    clearStagedMediaUpdates () {
      this.stagedMediaUpdates.clear();
    },

    reset () {
      this.cancelFetch();
      this.resetScheduledFilter();
      this.clearStagedMediaUpdates();
      this.$patch((state) => {
        state.pageIndex = 0;

        state.isFetchingFilesPage = false;
        state.filesPageMedia = [] as MediaListItem[];
        state.filesPageError = null;
        state.filesPageLastFetchedAt = null;

        state.isFetchingAlertsPage = false;
        state.alertsPageMedia = [] as MediaListItem[];
        state.alertsPageError = null;
        state.alertsPageLastFetchedAt = null;

        state.isFetchingLeds = false;
        state.ledMediaCanvasMap = new Map();
        state.ledPageError = null;

        state.isFetchingVideowall = false;
        state.videowallMediaCanvasMap = new Map();
        state.videowallPageError = null;
      });
    },

    resetFilesPage () {
      this.$patch((state) => {
        state.pageIndex = 0;
        state.isFetchingFilesPage = false;
        state.filesPageError = null;
        state.filesPageLastFetchedAt = null;
      });
    },

    resetAlertsPage () {
      this.$patch((state) => {
        state.pageIndex = 0;
        state.isFetchingAlertsPage = false;
        state.alertsPageError = null;
        state.alertsPageLastFetchedAt = null;
      });
    },

    resetLedPage () {
      this.$patch((state) => {
        state.isFetchingLeds = false;
        state.ledPageError = null;
      });
    },

    resetVideowallPage () {
      this.$patch((state) => {
        state.isFetchingVideowall = false;
        state.videowallPageError = null;
      });
    },

    resetScheduledFilter () {
      this.$patch((state) => {
        state.scheduledFilterMedia.value = null;
        state.scheduledFilterMedia.mediaItems = [];
        state.scheduledFilterMedia.isFetching = false;
        state.scheduledFilterMedia.pageContext = null;
        state.scheduledFilterMedia.ledDimension = undefined;
        state.scheduledFilterMedia.error = null;
        if (state.scheduledFilterMedia.abortController) {
          state.scheduledFilterMedia.abortController.abort();
          state.scheduledFilterMedia.abortController = null;
        }
      });
    }
  }
});
